import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Consultation Enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "Consultation Enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="px-6 md:px-12 lg:px-20 pt-24 pb-16 md:pt-32">
        <p className="overline mb-6">Get in Touch</p>
        <h1 className="text-5xl sm:text-7xl tracking-tight font-light max-w-4xl leading-[1.05]">
          Begin with an <em className="font-display italic" style={{ color: "var(--ayu-secondary)" }}>honest conversation.</em>
        </h1>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="p-10" style={{ background: "var(--ayu-surface)" }}>
            <p className="overline mb-6">Visit the Clinic</p>
            <ul className="space-y-6 text-sm">
              <li className="flex gap-4 items-start">
                <MapPin size={18} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} />
                <span>Ayurveda clinic, Near Shiv medico,<br />Siddhi terraces, lane no 18,<br />Raikar nagar, Dhayari, Pune</span>
              </li>
              <li className="flex gap-4 items-start">
                <Phone size={18} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} />
                <span>+91 90110 43798<br /><span className="opacity-60">Mon – Sat, 9am – 7pm IST</span></span>
              </li>
              <li className="flex gap-4 items-start">
                <MessageCircle size={18} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} />
                <span>WhatsApp: +91 90110 43798</span>
              </li>
              <li className="flex gap-4 items-start">
                <Mail size={18} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} />
                <span>care@ayurveda-pune-clinic.com</span>
              </li>
              <li className="flex gap-4 items-start">
                <Clock size={18} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} />
                <span>Mon – Sat · 9:00 – 19:00<br />Sunday by appointment</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 aspect-video overflow-hidden">
            <iframe
              title="Clinic location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=73.7900%2C18.4200%2C73.8300%2C18.4600&amp;layer=mapnik"
              className="w-full h-full"
              style={{ border: 0 }}
              loading="lazy"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-8">
            <div>
              <label className="overline block mb-2">Name</label>
              <input name="name" data-testid="contact-name" required value={form.name} onChange={onChange} className="ayu-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Email</label>
                <input name="email" type="email" data-testid="contact-email" required value={form.email} onChange={onChange} className="ayu-input" />
              </div>
              <div>
                <label className="overline block mb-2">Phone (optional)</label>
                <input name="phone" data-testid="contact-phone" value={form.phone} onChange={onChange} className="ayu-input" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Subject</label>
              <select name="subject" data-testid="contact-subject" value={form.subject} onChange={onChange} className="ayu-input">
                <option>Consultation Enquiry</option>
                <option>Panchakarma Programme</option>
                <option>Apothecary Order</option>
                <option>Equipment Enquiry</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Tell us what brings you here</label>
              <textarea name="message" data-testid="contact-message" required rows={6} value={form.message} onChange={onChange} className="ayu-input" />
            </div>
            <button type="submit" disabled={submitting} data-testid="contact-form-submit" className="btn-primary disabled:opacity-60">
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
